import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, CircularProgress, TextField, Box, Typography, InputAdornment } from '@mui/material';
import Search from '@mui/icons-material/Search';

import useDebouncer from 'src/common/useDebouncer';
import { useAppDispatch } from 'src/redux-app-hooks';
import { showSnackbar, NotificationSeverity } from 'src/features/notifications/notifications.slice';
import { Company } from '../crm.dto';
import { admiCompanyService } from '../admi-company.service';

export function CompanyAutocomplete({
    value,
    size,
    label,
    fullWidth,
    disableClearable,
    onSelectCompany,
}: {
    value?: Company | null;
    size?: 'small' | 'medium';
    /** Optional floating MUI label, mirrors `OwnerAutocomplete`. */
    label?: string;
    /**
     * Fill the parent container instead of the default fixed 350px min-width.
     * Use inside a flex/`FormControl` filter bar so the control can shrink and
     * wrap responsively rather than overflowing onto its neighbours.
     */
    fullWidth?: boolean;
    /**
     * Hide the clear (X) button. Use where the selection is required and a
     * cleared input would desync from the consumer's state (e.g. the
     * offer-evaluation bidder, whose clear is ignored).
     */
    disableClearable?: boolean;
    /** Fires with `null` when the user clears the input via the X. */
    onSelectCompany: (company: Company | null) => void;
}) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const debouncer = useDebouncer();

    function handleInputChange(_: unknown, value: string | null, reason: string) {
        // Only search when user types, not when selecting an option
        if (reason !== 'input' || !value) {
            return;
        }

        setLoading(true);

        debouncer(async () => {
            try {
                const companies = await admiCompanyService.searchCompanies(value);
                setCompanies(companies);
            } catch (error) {
                console.error('Failed to search companies:', error);
                dispatch(
                    showSnackbar({
                        message: t('Failed to search companies. Please try again.'),
                        severity: NotificationSeverity.Error,
                    }),
                );
                setCompanies([]);
            } finally {
                setLoading(false);
            }
        }, 1000);
    }

    function handleChange(_: unknown, value: Company | null) {
        onSelectCompany(value);
    }

    return (
        <Autocomplete
            fullWidth={fullWidth}
            disableClearable={disableClearable}
            options={value && !companies.some((c) => isSameCompany(c, value)) ? [value, ...companies] : companies}
            value={value ?? null}
            loading={loading}
            getOptionLabel={(option) => option.companyName}
            isOptionEqualToValue={isSameCompany}
            onInputChange={handleInputChange}
            onChange={handleChange}
            filterOptions={(x) => x} // this is required for the options to update in async mode
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={t('Search for a company')}
                    sx={fullWidth ? undefined : { minWidth: 350 }}
                    size={size}
                    slotProps={{
                        ...params.slotProps,
                        ...(label ? { inputLabel: { shrink: true } } : {}),
                        input: {
                            ...params.slotProps.input,
                            startAdornment: (
                                <>
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                    {params.slotProps.input.startAdornment}
                                </>
                            ),
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.slotProps.input.endAdornment}
                                </>
                            ),
                        },
                    }}
                />
            )}
            renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                    <li key={key || option.id} {...optionProps}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {option.companyName}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {option.address}, {option.city}
                            </Typography>
                        </Box>
                    </li>
                );
            }}
        />
    );
}

// A deep-linked value may only carry the HubSpot `externalId` (its `id` is
// then a placeholder), while search results are keyed by the data-products
// surrogate `id` — the same company can therefore arrive with two different
// `id`s. Match on either id space so the selected value pairs up with its
// search-result twin instead of appearing twice in the options.
function isSameCompany(a: Company, b: Company): boolean {
    return a.id === b.id || (!!a.externalId && a.externalId === b.externalId);
}
